import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Chip,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AssignmentIcon from '@mui/icons-material/Assignment';
import QrCodeIcon from '@mui/icons-material/QrCode';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/types/user.types';
import { getRoleDisplayName } from '@/utils/roles';

interface NavigationMenuProps {
  onItemClick?: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  requiredRole?: UserRole;
}

const mainNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { label: 'Products', path: '/products', icon: <InventoryIcon /> },
  { label: 'Orders', path: '/orders', icon: <ShoppingCartIcon /> },
];

const adminNavItems: NavItem[] = [
  {
    label: 'User Management',
    path: '/admin/users',
    icon: <PeopleIcon />,
    requiredRole: UserRole.ADMIN,
  },
  {
    label: 'Assign Products',
    path: '/admin/assign-products',
    icon: <AssignmentIcon />,
    requiredRole: UserRole.ADMIN,
  },
  {
    label: 'Upload Products',
    path: '/admin/upload',
    icon: <UploadFileIcon />,
    requiredRole: UserRole.ADMIN,
  },
  {
    label: 'QR Codes',
    path: '/admin/qr-codes',
    icon: <QrCodeIcon />,
    requiredRole: UserRole.ADMIN,
  },
];

const superAdminNavItems: NavItem[] = [
  {
    label: 'Transaction Log',
    path: '/admin/transactions',
    icon: <HistoryIcon />,
    requiredRole: UserRole.SUPER_ADMIN,
  },
  {
    label: 'Settings',
    path: '/admin/settings',
    icon: <SettingsIcon />,
    requiredRole: UserRole.SUPER_ADMIN,
  },
];

export default function NavigationMenu({ onItemClick }: NavigationMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, isAdmin, isSuperAdmin } = useAuth();

  const handleNavigation = (path: string) => {
    navigate(path);
    onItemClick?.();
  };

  const renderNavItems = (items: NavItem[]) => {
    return items.map((item) => {
      // Check role requirement
      if (item.requiredRole) {
        if (item.requiredRole === UserRole.SUPER_ADMIN && !isSuperAdmin) {
          return null;
        }
        if (item.requiredRole === UserRole.ADMIN && !isAdmin) {
          return null;
        }
      }

      const isActive = location.pathname === item.path;

      return (
        <ListItem key={item.path} disablePadding>
          <ListItemButton
            selected={isActive}
            onClick={() => handleNavigation(item.path)}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
                '& .MuiListItemIcon-root': {
                  color: 'primary.main',
                },
                '& .MuiListItemText-primary': {
                  color: 'primary.main',
                  fontWeight: 600,
                },
              },
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        </ListItem>
      );
    });
  };

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      {/* User Role Badge */}
      {userData && (
        <Box sx={{ px: 2, py: 1 }}>
          <Chip
            label={getRoleDisplayName(userData.role)}
            size="small"
            color={
              userData.role === UserRole.SUPER_ADMIN
                ? 'error'
                : userData.role === UserRole.ADMIN
                  ? 'primary'
                  : 'default'
            }
            sx={{ width: '100%' }}
          />
        </Box>
      )}

      {/* Main Navigation */}
      <List>
        {renderNavItems(mainNavItems)}
      </List>

      {/* Admin Navigation */}
      {isAdmin && (
        <>
          <Divider sx={{ my: 1 }} />
          <List subheader={
            <Box sx={{ px: 2, py: 0.5 }}>
              <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                ADMIN
              </Box>
            </Box>
          }>
            {renderNavItems(adminNavItems)}
          </List>
        </>
      )}

      {/* Super Admin Navigation */}
      {isSuperAdmin && (
        <>
          <Divider sx={{ my: 1 }} />
          <List subheader={
            <Box sx={{ px: 2, py: 0.5 }}>
              <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                SUPER ADMIN
              </Box>
            </Box>
          }>
            {renderNavItems(superAdminNavItems)}
          </List>
        </>
      )}

      {/* Company Name at Bottom */}
      {userData?.companyName && (
        <Box sx={{ mt: 'auto', p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Company</Box>
          <Box sx={{ fontWeight: 500 }}>{userData.companyName}</Box>
        </Box>
      )}
    </Box>
  );
}
